import React, { useState, useEffect, useMemo } from "react";
import {
  Modal,
  Button,
  Form,
  Row,
  Col,
  Table,
  Badge,
  InputGroup,
  Alert,
  Card,
} from "react-bootstrap";
import {
  IconArrowRight,
  IconPackage,
  IconCheck,
  // IconX,
  IconSearch,
  IconSelectAll,
  IconSortAscending,
  IconSortDescending,
  // IconDeselect,
} from "@tabler/icons-react";
import { postRequest, getRequest } from "services/api";
import { useCategory } from "../context/CategoryContext";
import Swal from "sweetalert2";
import { useAuth } from "hooks/useAuth";
import AsyncSelect from "react-select/async";

const BulkTransferModal = ({
  show,
  onHide,
  onSuccess,
  onError,
  warehouses = [],
}) => {
  const { user } = useAuth();
  const { categories } = useCategory();
  const [bulkTransfers, setBulkTransfers] = useState([]);
  const [selectedMechanic, setSelectedMechanic] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [stockFilter, setStockFilter] = useState("all");
  const [activeFilter, setActiveFilter] = useState("active");
  const [orderBy, setOrderBy] = useState("name");
  const [sort, setSort] = useState("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const [allInventoryData, setAllInventoryData] = useState([]);
  const [allCategoriesData, setAllCategoriesData] = useState([]);
  const [fromWarehouseId, setFromWarehouseId] = useState("");
  const [toWarehouseId, setToWarehouseId] = useState("");
  const [responsibleStaffId, setResponsibleStaffId] = useState("");
  const [notes, setNotes] = useState("");
  const [transferDate, setTransferDate] = useState(
    new Date().toLocaleString('sv-SE', { timeZone: 'Asia/Makassar' }).slice(0, 16),
  );
  const [imagePreview, setImagePreview] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);

  // Fetch all inventory data for bulk transfers
  const fetchAllInventory = async () => {
    try {
      const response = await getRequest(
        "/inventory?limit=10000&include=category",
      );
      if (response.success) {
        setAllInventoryData(response.data || []);
        return response.data || [];
      }
      return [];
    } catch (error) {
      console.error("Failed to fetch all inventory:", error);
      return [];
    }
  };

  // Fetch all categories for bulk transfers
  const fetchAllCategories = async () => {
    try {
      const response = await getRequest("/inventory-category?limit=10000");
      if (response.success) {
        setAllCategoriesData(response.data || []);
        return response.data || [];
      }
      return [];
    } catch (error) {
      console.error("Failed to fetch all categories:", error);
      return [];
    }
  };

  useEffect(() => {
    if (show) {
      // Reset form
      setBulkTransfers([]);
      setSearchTerm("");
      setCategoryFilter("");
      setStockFilter("all");
      setCurrentPage(1);
      setFromWarehouseId(warehouses.length > 0 ? warehouses[0].id : "");
      setToWarehouseId(warehouses.length > 1 ? warehouses[1].id : "");
      setResponsibleStaffId(""); // Will be set from current user
      setSelectedMechanic(null);
      setNotes("");
      setTransferDate(new Date().toLocaleString('sv-SE', { timeZone: 'Asia/Makassar' }).slice(0, 16));

      // Fetch all inventory data
      fetchAllInventory().then((allData) => {
        setBulkTransfers(
          allData.map((item) => ({
            id: item.id,
            name: item.name,
            warehouseStocks: item.warehouse_stocks || [],
            quantity: 0,
            selected: false,
            category: item.category?.name || "Uncategorized",
            category_id: item.category_id,
            unit: item.unit,
            price: parseFloat(item.price) || 0,
            image_url: item.image_url,
            is_active: item.is_active !== false,
          })),
        );
      });
      fetchAllCategories();
    }
  }, [show, warehouses]);

  // Filter bulk items
  const filteredBulkItems = useMemo(() => {
    let filtered = bulkTransfers;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter((item) =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }

    // Category filter
    if (categoryFilter) {
      const categoryItems = allInventoryData.filter(
        (item) => item.category_id === categoryFilter,
      );
      const categoryItemIds = categoryItems.map((item) => item.id);
      filtered = filtered.filter((item) => categoryItemIds.includes(item.id));
    }

    // Stock filter
    if (stockFilter !== "all") {
      filtered = filtered.filter((item) => {
        const fromWarehouseStock = item.warehouseStocks?.find(
          (ws) => ws.warehouse_id === fromWarehouseId,
        );
        const toWarehouseStock = item.warehouseStocks?.find(
          (ws) => ws.warehouse_id === toWarehouseId,
        );
        const fromStock = fromWarehouseStock ? fromWarehouseStock.stock : 0;
        const toStock = toWarehouseStock ? toWarehouseStock.stock : 0;

        if (stockFilter === "from_available") {
          return fromStock > 0;
        } else if (stockFilter === "from_low") {
          return fromStock > 0 && fromStock <= 10;
        } else if (stockFilter === "to_low") {
          return toStock <= 5;
        }
        return true;
      });
    }

    // Active filter
    if (activeFilter === "active") {
      filtered = filtered.filter((item) => item.is_active);
    } else if (activeFilter === "inactive") {
      filtered = filtered.filter((item) => !item.is_active);
    }

    // Sort
    if (orderBy) {
      filtered = [...filtered].sort((a, b) => {
        let valA = a[orderBy];
        let valB = b[orderBy];

        if (typeof valA === 'string') valA = valA.toLowerCase();
        if (typeof valB === 'string') valB = valB.toLowerCase();

        if (valA < valB) return sort === "asc" ? -1 : 1;
        if (valA > valB) return sort === "asc" ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [
    bulkTransfers,
    searchTerm,
    categoryFilter,
    stockFilter,
    activeFilter,
    orderBy,
    sort,
    allInventoryData,
  ]);

  // Pagination
  const itemsPerPage = 10;
  const totalPages = Math.ceil(filteredBulkItems.length / itemsPerPage);
  const paginatedItems = filteredBulkItems.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const selectedItems = bulkTransfers.filter((item) => item.selected);
  const totalSelectedQuantity = selectedItems.reduce(
    (sum, item) => sum + item.quantity,
    0,
  );
  const totalSelectedValue = selectedItems.reduce(
    (sum, item) => sum + item.quantity * item.price,
    0,
  );

  const handleSelectItem = (itemId) => {
    setBulkTransfers((prev) =>
      prev.map((item) =>
        item.id === itemId
          ? {
            ...item,
            selected: !item.selected,
            quantity: item.selected ? 0 : 1,
          }
          : item,
      ),
    );
  };

  const handleSelectAll = () => {
    const allSelected = paginatedItems.every((item) => item.selected);
    setBulkTransfers((prev) =>
      prev.map((item) =>
        paginatedItems.find((p) => p.id === item.id)
          ? { ...item, selected: !allSelected, quantity: !allSelected ? 1 : 0 }
          : item,
      ),
    );
  };

  const handleQuantityChange = (itemId, quantity) => {
    const qty = Math.max(0, parseInt(quantity) || 0);
    setBulkTransfers((prev) =>
      prev.map((item) =>
        item.id === itemId ? { ...item, quantity: qty } : item,
      ),
    );
  };

  const handleSubmit = async () => {
    if (!fromWarehouseId || !toWarehouseId) {
      Swal.fire(
        "Error!",
        "Please select both source and destination warehouses",
        "error",
      );
      return;
    }

    if (fromWarehouseId === toWarehouseId) {
      Swal.fire(
        "Error!",
        "Source and destination warehouses must be different",
        "error",
      );
      return;
    }

    const itemsToTransfer = selectedItems.filter((item) => item.quantity > 0);

    if (itemsToTransfer.length === 0) {
      Swal.fire(
        "Error!",
        "Please select items and specify quantities to transfer.",
        "error",
      );
      return;
    }

    // Validate quantities
    const invalidItems = itemsToTransfer.filter((item) => {
      const fromWarehouseStock = item.warehouseStocks?.find(
        (ws) => ws.warehouse_id === fromWarehouseId,
      );
      const fromStock = fromWarehouseStock ? fromWarehouseStock.stock : 0;
      return item.quantity > fromStock;
    });

    if (invalidItems.length > 0) {
      Swal.fire(
        "Error!",
        `Insufficient stock for: ${invalidItems.map((i) => i.name).join(", ")}`,
        "error",
      );
      return;
    }

    setLoading(true);

    try {
      const staffId =
        responsibleStaffId || user?.id || user?.staff_id || "";

      // Bulk transfer API call with new structure
      const transferData = {
        from_warehouse_id: fromWarehouseId,
        to_warehouse_id: toWarehouseId,
        responsible_staff_id: staffId,
        notes: notes || "Bulk transfer between warehouses",
        transfer_date: transferDate,
        items: itemsToTransfer.map((item) => ({
          inventory_id: item.id,
          quantity: item.quantity,
          price: item.price,
        })),
      };

      const response = await postRequest("/stock-transfers", transferData);

      if (response.success) {
        Swal.fire(
          "Success!",
          `Successfully transferred ${itemsToTransfer.length} items (${totalSelectedQuantity} units total).`,
          "success",
        );
        onSuccess && onSuccess();
        onHide();
      } else {
        throw new Error(response.message || "Bulk transfer failed");
      }
    } catch (error) {
      const errorMessage = error.message || "Failed to perform bulk transfer";
      Swal.fire("Error!", errorMessage, "error");
      onError && onError(error);
    } finally {
      setLoading(false);
    }
  };

  const getStockStatus = (warehouseStocks, fromWarehouseId, toWarehouseId) => {
    const fromStock =
      warehouseStocks?.find((ws) => ws.warehouse_id === fromWarehouseId)
        ?.stock || 0;
    const toStock =
      warehouseStocks?.find((ws) => ws.warehouse_id === toWarehouseId)?.stock ||
      0;
    const total = fromStock + toStock;

    if (total === 0)
      return { status: "empty", color: "#dc2626", label: "Empty" };
    if (total <= 10) return { status: "low", color: "#d97706", label: "Low" };
    return { status: "good", color: "#059669", label: "Good" };
  };


  const handleSort = (field) => {
    if (orderBy === field) {
      setSort(sort === "asc" ? "desc" : "asc");
    } else {
      setOrderBy(field);
      setSort("asc");
    }
  };

  const renderSortableHeader = (label, field, style = {}) => {
    const isSorted = orderBy === field;
    const isAsc = sort === "asc";

    return (
      <th
        onClick={() => handleSort(field)}
        style={{ cursor: "pointer", userSelect: "none", ...style }}
        title={`Sort by ${label}`}
      >
        <div className={`d-flex align-items-center gap-1 ${style.textAlign === 'center' ? 'justify-content-center' : 'justify-content-between'}`}>
          <span>{label}</span>
          {isSorted ? (
            isAsc ? <IconSortAscending size={14} className="text-primary" /> : <IconSortDescending size={14} className="text-primary" />
          ) : (
            <IconSortDescending size={14} className="text-muted opacity-25" />
          )}
        </div>
      </th>
    );
  };

  return (
    <>
      <Modal className="workshop-modal-scope" show={show} onHide={onHide} fullscreen={true} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <IconArrowRight size={20} style={{ marginRight: "8px" }} />
            Bulk Transfer Between Warehouses
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Transfer Configuration */}
          <Card className="mb-3">
            <Card.Body>
              <Row className="g-3">
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>From Warehouse *</Form.Label>
                    <Form.Select
                      value={fromWarehouseId}
                      onChange={(e) => setFromWarehouseId(e.target.value)}
                    >
                      <option value="">Select Source Warehouse</option>
                      {warehouses.map((warehouse) => (
                        <option key={warehouse.id} value={warehouse.id}>
                          {warehouse.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>To Warehouse *</Form.Label>
                    <Form.Select
                      value={toWarehouseId}
                      onChange={(e) => setToWarehouseId(e.target.value)}
                    >
                      <option value="">Select Destination Warehouse</option>
                      {warehouses.map((warehouse) => (
                        <option key={warehouse.id} value={warehouse.id}>
                          {warehouse.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Transfer Date & Time</Form.Label>
                    <Form.Control
                      type="datetime-local"
                      value={transferDate}
                      onChange={(e) => setTransferDate(e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Penanggung Jawab (Mekanik)</Form.Label>
                    <AsyncSelect classNamePrefix="react-select" className="react-select-container"
                      loadOptions={async (inputValue) => {
                        try {
                          const response = await getRequest("/user?all=1&include=roles");
                          if (response.success) {
                            return response.data
                              .filter(
                                (u) =>
                                  u.roles?.some((role) => role.name === "mekanik") &&
                                  u.name.toLowerCase().includes(inputValue.toLowerCase())
                              )
                              .map((u) => ({
                                value: u.id,
                                label: u.name,
                              }));
                          }
                          return [];
                        } catch (error) {
                          console.error("Error loading mechanics:", error);
                          return [];
                        }
                      }}
                      value={selectedMechanic}
                      onChange={(selected) => {
                        setResponsibleStaffId(selected ? selected.value : "");
                        setSelectedMechanic(selected);
                      }}
                      isSearchable
                      placeholder="Pilih mekanik..."
                      isClearable
                      defaultOptions={true}
                      cacheOptions={true}
                    />
                  </Form.Group>
                </Col>

              </Row>
            </Card.Body>

            <Card.Body>
              <Row className="g-3">
                <Col md={4}>
                  <InputGroup>
                    <InputGroup.Text>
                      <IconSearch size={16} />
                    </InputGroup.Text>
                    <Form.Control
                      placeholder="Search items..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </InputGroup>
                </Col>
                <Col md={4}>
                  <Form.Select
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                  >
                    <option value="">All Categories</option>
                    {allCategoriesData.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
                <Col md={4}>
                  <Form.Select
                    value={stockFilter}
                    onChange={(e) => setStockFilter(e.target.value)}
                  >
                    <option value="all">All Items</option>
                    <option value="from_available">
                      Source Warehouse Stock Available
                    </option>
                    <option value="from_low">
                      Source Warehouse Low Stock (≤10)
                    </option>
                    <option value="to_low">
                      Destination Warehouse Low Stock (≤5)
                    </option>
                  </Form.Select>
                </Col>
                <Col md={4}>
                  <Form.Select
                    value={activeFilter}
                    onChange={(e) => setActiveFilter(e.target.value)}
                  >
                    <option value="all">Semua Status Aktif</option>
                    <option value="active">Aktif Saja</option>
                    <option value="inactive">Tidak Aktif Saja</option>
                  </Form.Select>
                </Col>
                <Col md={8}>
                  <Form.Group>
                    <Form.Label>Notes</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={1}
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Transfer notes..."
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Summary */}
          {selectedItems.length > 0 && (
            <Alert variant="success" className="mb-3">
              <strong>Selected:</strong> {selectedItems.length} items,{" "}
              {totalSelectedQuantity} units total
              {/* <strong>
              Total Value: Rp {totalSelectedValue.toLocaleString("id-ID")}
            </strong> */}
            </Alert>
          )}

          {/* Table */}
          <div style={{ maxHeight: "1000px", overflowY: "auto" }}>
            <Table striped bordered hover size="sm">
              <thead
                style={{ position: "sticky", top: 0, backgroundColor: "white" }}
              >
                <tr>
                  <th style={{ width: "40px" }}>
                    <Form.Check
                      type="checkbox"
                      checked={
                        paginatedItems.length > 0 &&
                        paginatedItems.every((item) => item.selected)
                      }
                      onChange={handleSelectAll}
                    />
                  </th>
                  {renderSortableHeader("Item", "name")}
                  {renderSortableHeader("Category", "category")}
                  {renderSortableHeader("Price per Unit", "price", { textAlign: "center" })}
                  <th style={{ textAlign: "center" }}>
                    {warehouses.find((w) => w.id === fromWarehouseId)?.name ||
                      "Source"}{" "}
                    Stock
                  </th>
                  <th style={{ textAlign: "center" }}>
                    {warehouses.find((w) => w.id === toWarehouseId)?.name ||
                      "Destination"}{" "}
                    Stock
                  </th>
                  <th style={{ textAlign: "center", width: "120px" }}>
                    Transfer Qty
                  </th>
                  <th style={{ textAlign: "center" }}>After Transfer</th>
                  <th style={{ textAlign: "center" }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {paginatedItems.map((item) => {
                  const fromWarehouseStock = item.warehouseStocks?.find(
                    (ws) => ws.warehouse_id === fromWarehouseId,
                  );
                  const toWarehouseStock = item.warehouseStocks?.find(
                    (ws) => ws.warehouse_id === toWarehouseId,
                  );
                  const fromStock = fromWarehouseStock
                    ? fromWarehouseStock.stock
                    : 0;
                  const toStock = toWarehouseStock ? toWarehouseStock.stock : 0;
                  const stockStatus = getStockStatus(
                    item.warehouseStocks,
                    fromWarehouseId,
                    toWarehouseId,
                  );

                  return (
                    <tr key={item.id}>
                      <td>
                        <Form.Check
                          type="checkbox"
                          checked={item.selected}
                          onChange={() => handleSelectItem(item.id)}
                          disabled={fromStock === 0}
                        />
                      </td>
                      <td>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          {item.image_url && (
                            <img
                              src={item.image_url}
                              alt={item.name}
                              onClick={() => {
                                setImagePreview({ src: item.image_url, alt: item.name });
                                setShowImageModal(true);
                              }}
                              style={{
                                width: "40px",
                                height: "40px",
                                objectFit: "cover",
                                borderRadius: "4px",
                                border: "1px solid #dee2e6",
                                cursor: "pointer"
                              }}
                            />
                          )}
                          <div>
                            <div style={{ fontWeight: "500" }}>{item.name}</div>
                            {fromStock === 0 && (
                              <small className="text-muted">
                                No stock available in source warehouse
                              </small>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>{item.category}</td>
                      <td style={{ textAlign: "center" }}>
                        Rp {item.price.toLocaleString("id-ID")}
                      </td>
                      <td style={{ textAlign: "center", fontWeight: "500" }}>
                        {fromStock} {item.unit}
                      </td>
                      <td style={{ textAlign: "center", fontWeight: "500" }}>
                        {toStock} {item.unit}
                      </td>
                      <td>
                        <Form.Control
                          type="number"
                          min="0"
                          max={fromStock}
                          value={item.quantity}
                          onChange={(e) =>
                            handleQuantityChange(item.id, e.target.value)
                          }
                          disabled={!item.selected}
                          size="sm"
                          style={{ textAlign: "center" }}
                        />
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <small>
                          {warehouses.find((w) => w.id === fromWarehouseId)
                            ?.name || "Source"}
                          : {fromStock - item.quantity} |{" "}
                          {warehouses.find((w) => w.id === toWarehouseId)?.name ||
                            "Destination"}
                          : {toStock + item.quantity}
                        </small>
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <Badge
                          style={{
                            backgroundColor: stockStatus.color,
                            color: "white",
                          }}
                        >
                          {stockStatus.label}
                        </Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-center mt-3">
              <div className="d-flex gap-2">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(currentPage - 1)}
                >
                  Previous
                </Button>
                <span className="align-self-center">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(currentPage + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}

          {filteredBulkItems.length === 0 && (
            <div className="text-center py-4">
              <IconPackage size={48} className="text-muted mb-3" />
              <p className="text-muted">No items found matching your criteria.</p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={
              loading || selectedItems.length === 0 || totalSelectedQuantity === 0
            }
          >
            {loading ? (
              "Transferring..."
            ) : (
              <>
                <IconCheck size={16} style={{ marginRight: "8px" }} />
                Transfer {selectedItems.length} Items ({totalSelectedQuantity}{" "}
                units)
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Image Preview Modal */}
      <Modal
        show={showImageModal}
        onHide={() => setShowImageModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{imagePreview?.alt || "Image Preview"}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {imagePreview && (
            <img
              src={imagePreview.src}
              alt={imagePreview.alt}
              style={{
                maxWidth: "100%",
                maxHeight: "70vh",
                objectFit: "contain",
                borderRadius: "8px",
                boxShadow: "0 4px 8px rgba(0,0,0,0.1)"
              }}
            />
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default BulkTransferModal;
